import { toaster } from '@m/components-ui/toaster';
import {
  LocationSubscriptionPayload,
  LocationSubscriptionPlan,
  SubscriptionPlanCreateForm,
  WhitelistedPhoneForm,
  WhitelistedEmailForm,
} from '@m/types/api';
import { apiFetchV2 } from '@m/utils/http';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IndividualSubscriptionUser } from 'apps/admin/containers/location/IndividualSubscriptionContainer/types';

function invalidateIndividualSubscriptionByIdQuery({
  queryClient,
  subscriptionPlanId,
}: {
  queryClient: QueryClient;
  subscriptionPlanId: number;
}) {
  return queryClient.invalidateQueries({
    queryKey: ['individualSubscriptionById', subscriptionPlanId.toString()],
  });
}

function invalidateSubscriptionsBySiteAll({ queryClient }: { queryClient: QueryClient }) {
  return queryClient.invalidateQueries({
    queryKey: ['individualSubscriptionsBySite'],
  });
}
function invalidateWhiteListedPhone({
  queryClient,
  subscriptionPlanId,
}: {
  queryClient: QueryClient;
  subscriptionPlanId: number;
}) {
  return queryClient.invalidateQueries({
    queryKey: ['whitelistedPhones', subscriptionPlanId],
  });
}

function invalidateWhiteListedEmails({
  queryClient,
  subscriptionPlanId,
}: {
  queryClient: QueryClient;
  subscriptionPlanId: number;
}) {
  return queryClient.invalidateQueries({
    queryKey: ['whitelistedEmails', subscriptionPlanId],
  });
}

function invalidateIndividualSubscriptionsBySiteQuery({
  queryClient,
  siteId,
}: {
  queryClient: QueryClient;
  siteId: string;
}) {
  return queryClient.invalidateQueries({
    queryKey: ['individualSubscriptionsBySite', siteId],
  });
}

function invalidateIndividualSubscriptionUsersQuery({
  queryClient,
  subscriptionPlanId,
}: {
  queryClient: QueryClient;
  subscriptionPlanId: number;
}) {
  return queryClient.invalidateQueries({
    queryKey: ['individualSubscriptionUsers', subscriptionPlanId],
  });
}

export const fetchIndividualSubscriptionsBySite = (siteId: string) =>
  apiFetchV2<LocationSubscriptionPlan[]>(`/admin/subscription/plans/site/${siteId}`);

export const useIndividualSubscriptionsBySiteQuery = (siteId: string | undefined) =>
  useQuery({
    queryKey: ['individualSubscriptionsBySite', siteId],
    queryFn: async () => {
      if (!siteId) return [];
      return fetchIndividualSubscriptionsBySite(siteId);
    },
    enabled: Boolean(siteId),
  });

export const useIndividualSubscriptionByIdQuery = (planId: string | undefined) =>
  useQuery({
    queryKey: ['individualSubscriptionById', planId],
    queryFn: async () => apiFetchV2<LocationSubscriptionPlan>(`/admin/subscription/plan/${planId}`),
    enabled: Boolean(planId),
  });

const createIndividualSubscription = ({
  siteId,
  subscriptionPlanCreateForm,
}: {
  siteId: string;
  subscriptionPlanCreateForm: SubscriptionPlanCreateForm;
}) =>
  apiFetchV2<LocationSubscriptionPlan>(`/v2/admin/subscription/plan/site/${siteId}`, {
    method: 'POST',
    body: subscriptionPlanCreateForm,
  });

export const useCreateIndividualSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createIndividualSubscription,
    onSuccess: (data, variables) => {
      toaster.create({
        type: 'success',
        title: 'Subscription plan created successfully',
      });

      // Invalidate the site subscriptions query to refetch the list
      invalidateIndividualSubscriptionsBySiteQuery({
        queryClient,
        siteId: variables.siteId,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to create subscription plan',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const cancelCustomerSubscription = ({
  subscriptionId,
  userId,
  subscriptionCancellationReasonId,
}: {
  subscriptionCancellationReasonId?: number;
  subscriptionId: number;
  userId: number;
}) =>
  apiFetchV2(`/admin/customers/subscription/${subscriptionId}/cancel`, {
    method: 'POST',
    params: { userId },
    body: { subscriptionCancellationReasonId },
  });

export const useCancelCustomerSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelCustomerSubscription,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Subscription cancelled successfully',
      });

      // Invalidate individual subscription users query to reflect the cancellation
      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionId,
      });

      // Invalidate site subscriptions query to refresh member counts
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to cancel subscription',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

const updateIndividualSubscription = ({
  subscriptionPlanId,
  subscriptionPlan,
}: {
  subscriptionPlan: LocationSubscriptionPayload;
  subscriptionPlanId: number;
}) =>
  apiFetchV2<LocationSubscriptionPlan>(`/admin/subscription/plan/${subscriptionPlanId}`, {
    method: 'PUT',
    body: subscriptionPlan,
  });

const deleteIndividualSubscription = ({ subscriptionPlanId }: { subscriptionPlanId: number }) =>
  apiFetchV2<{ deleted: boolean }>(`/admin/subscription/plan/${subscriptionPlanId}`, {
    method: 'DELETE',
  });

export const useUpdateIndividualSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateIndividualSubscription,
    onSuccess: (data) => {
      toaster.create({
        type: 'success',
        title: 'Subscription updated successfully',
      });
      const subId = data?.subscriptionPlan.id;
      const siteId = data?.subscriptionPlanSingleSite.siteId;
      if (subId) {
        invalidateIndividualSubscriptionByIdQuery({
          queryClient,
          subscriptionPlanId: subId,
        });
      }

      if (siteId) {
        invalidateIndividualSubscriptionsBySiteQuery({
          queryClient,
          siteId: siteId.toString(),
        });
      }
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to update subscription',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const useDeleteIndividualSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteIndividualSubscription,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Subscription deleted successfully',
      });

      // Invalidate the specific subscription query
      invalidateIndividualSubscriptionByIdQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });

      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to delete subscription',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const useIndividualSubscriptionUsersQuery = (subscriptionPlanId: number | undefined) =>
  useQuery({
    queryKey: ['individualSubscriptionUsers', subscriptionPlanId],
    queryFn: async () =>
      apiFetchV2<IndividualSubscriptionUser[]>(
        `/admin/subscription/plan/${subscriptionPlanId}/users`,
      ),
    enabled: Boolean(subscriptionPlanId),
  });

// Whitelisted Phone Functions
const getWhitelistedPhones = ({ subscriptionPlanId }: { subscriptionPlanId: number }) =>
  apiFetchV2<{ addedAt: number; id: number; phoneNumber: string }[]>(
    `/admin/subscription/plan/${subscriptionPlanId}/whitelisted-phones`,
  );

const addWhitelistedPhones = ({
  subscriptionPlanId,
  whitelistedPhoneForm,
}: {
  subscriptionPlanId: number;
  whitelistedPhoneForm: WhitelistedPhoneForm;
}) =>
  apiFetchV2<{ success: boolean }>(
    `/admin/subscription/plan/${subscriptionPlanId}/whitelisted-phones`,
    {
      method: 'POST',
      body: whitelistedPhoneForm,
    },
  );

const deleteWhitelistedPhone = ({
  subscriptionPlanId,
  whitelistedPhoneNumber,
}: {
  subscriptionPlanId: number;
  whitelistedPhoneNumber: string;
}) =>
  apiFetchV2<{ success: boolean }>(
    `/v2/admin/subscription/plan/${subscriptionPlanId}/whitelisted-phone/${whitelistedPhoneNumber}`,
    {
      method: 'DELETE',
    },
  );

export const useWhitelistedPhonesQuery = (subscriptionPlanId: number | undefined) =>
  useQuery({
    queryKey: ['whitelistedPhones', subscriptionPlanId],
    queryFn: () => getWhitelistedPhones({ subscriptionPlanId: subscriptionPlanId! }),
    enabled: Boolean(subscriptionPlanId),
  });

export const useAddWhitelistedPhonesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addWhitelistedPhones,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Phone number added to allowlist successfully',
      });

      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateWhiteListedPhone({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to add phone number to allowlist',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const useDeleteWhitelistedPhoneMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWhitelistedPhone,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Phone number removed from allowlist successfully',
      });
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
      // Invalidate the allowlisted phones query
      invalidateWhiteListedPhone({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to remove phone number from allowlist',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

// Whitelisted Email Functions
const getWhitelistedEmails = ({ subscriptionPlanId }: { subscriptionPlanId: number }) =>
  apiFetchV2<{ addedAt: number; email: string; id: number }[]>(
    `/admin/subscription/plan/${subscriptionPlanId}/whitelisted-emails`,
  );

const addWhitelistedEmails = ({
  subscriptionPlanId,
  whitelistedEmailForm,
}: {
  subscriptionPlanId: number;
  whitelistedEmailForm: WhitelistedEmailForm;
}) =>
  apiFetchV2<{ success: boolean }>(
    `/admin/subscription/plan/${subscriptionPlanId}/whitelisted-emails`,
    {
      method: 'POST',
      body: whitelistedEmailForm,
    },
  );

const deleteWhitelistedEmail = ({
  whitelistedEmailId,
  subscriptionPlanId,
}: {
  subscriptionPlanId: number;
  whitelistedEmailId: number;
}) =>
  apiFetchV2<{ success: boolean }>(
    `/admin/subscription/plan/${subscriptionPlanId}/whitelisted-email/${whitelistedEmailId}`,
    {
      method: 'DELETE',
    },
  );

const deleteWhitelistedEmailByEmail = ({
  whitelistedEmail,
  subscriptionPlanId,
}: {
  subscriptionPlanId: number;
  whitelistedEmail: string;
}) =>
  apiFetchV2<{ success: boolean }>(
    `/v2/admin/subscription/plan/${subscriptionPlanId}/whitelisted-email/${whitelistedEmail}`,
    {
      method: 'DELETE',
    },
  );

export const useWhitelistedEmailsQuery = (subscriptionPlanId: number | undefined) =>
  useQuery({
    queryKey: ['whitelistedEmails', subscriptionPlanId],
    queryFn: () => getWhitelistedEmails({ subscriptionPlanId: subscriptionPlanId! }),
    enabled: Boolean(subscriptionPlanId),
  });

export const useAddWhitelistedEmailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addWhitelistedEmails,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Email address added to allowlist successfully',
      });
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
      // Invalidate the whitelisted emails query
      invalidateWhiteListedEmails({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to add email address to allowlist',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const useDeleteWhitelistedEmailMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWhitelistedEmail,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Email address removed from allowlist successfully',
      });
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
      // Invalidate the whitelisted emails query
      invalidateWhiteListedEmails({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to remove email address from allowlist',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};

export const useDeleteWhitelistedEmailByEmailMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWhitelistedEmailByEmail,
    onSuccess: (_, variables) => {
      toaster.create({
        type: 'success',
        title: 'Email address removed from allowlist successfully',
      });
      invalidateSubscriptionsBySiteAll({
        queryClient,
      });
      // Invalidate the whitelisted emails query
      invalidateWhiteListedEmails({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
      invalidateIndividualSubscriptionUsersQuery({
        queryClient,
        subscriptionPlanId: variables.subscriptionPlanId,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: 'Failed to remove email address from allowlist',
        description: error.message || 'An error occurred while processing your request.',
      });
    },
  });
};
